// A freshly issued invitation or password reset code, shown once to the admin who issued it (only its
// hash is stored). It stays until closed so the admin can copy it into their own
// email or a Teams message when our email is blocked by the person's company.
import React, { useEffect, useState } from 'react';
import './InvitationCodeNotice.css';

export function formatExpiry(iso) {
  return new Date(iso).toLocaleDateString('en-US', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
  });
}

export default function InvitationCodeNotice({ invitation, onClose }) {
  const [copied, setCopied] = useState(false);
  // A different code means the clipboard no longer holds what the label claims.
  useEffect(() => { setCopied(false); }, [invitation?.code]);
  if (!invitation) return null;

  const header = (title) => (
    <div className="invite-notice__head">
      <strong>{title}</strong>
      <button type="button" className="invite-notice__close" onClick={onClose} aria-label="Close">×</button>
    </div>
  );

  // Codes for people at another company aren't shown to client managers (design
  // revision 2026-10-19, D2-B); the person still gets the email.
  if (invitation.code_hidden) {
    return (
      <div className="invite-notice" role="status">
        {header(`Invitation code for ${invitation.email}`)}
        <p className="invite-notice__text">
          {invitation.email_sent ? 'We emailed them an invitation code.' : 'We couldn’t email them an invitation code.'}
          {' '}Codes are shown only to administrators at the person’s own company. If it doesn’t arrive,
          {' '}ask RDV Systems support (inquiries@rdvsystems.com) for a new code.
        </p>
      </div>
    );
  }

  const isReset = invitation.purpose === 'reset';
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(invitation.code);
      setCopied(true);
    } catch (err) {
      setCopied(false);
    }
  };

  return (
    <div className="invite-notice" role="status">
      {header(`${isReset ? 'Password reset code' : 'Invitation code'} for ${invitation.email}`)}
      <div className="invite-notice__code-row">
        <code className="invite-notice__code">{invitation.code}</code>
        <button type="button" className="invite-notice__copy" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
      </div>
      <p className="invite-notice__text">
        {invitation.email_sent
          ? 'We emailed this code to them. If it doesn’t arrive, send it yourself (Teams, phone or your own email).'
          : 'We couldn’t email this code. Send it to them yourself (Teams, phone or your own email).'}
        {' '}
        {isReset
          ? `They reset their password at ${window.location.host}/signup?reset=1, then confirm with their usual sign-in code.`
          : `They create their account at ${window.location.host}/signup.`}
        {' '}The code works once and expires on {formatExpiry(invitation.expires_at)}.
      </p>
    </div>
  );
}
